/**
 * Iter 122 — Safe clipboard copy with fallback.
 *
 * Modern `navigator.clipboard.writeText` fails with NotAllowedError when the
 * page is embedded in an iframe with a restrictive Permissions-Policy (which
 * is the case for our preview URLs). Falls back to the legacy
 * `document.execCommand('copy')` via a hidden textarea, and — if that also
 * fails — shows a toast with the raw text so the user can copy manually.
 *
 * Returns true on success, false otherwise.
 */
import { toast } from 'sonner';

export async function copyToClipboard(text, { silent = false, label = 'Copied to clipboard' } = {}) {
  if (text === null || text === undefined) return false;
  const str = String(text);

  // Modern async Clipboard API (works everywhere EXCEPT restricted iframes).
  if (typeof navigator !== 'undefined' && navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(str);
      if (!silent) toast.success(label);
      return true;
    } catch (_) {
      /* fall through to execCommand */
    }
  }

  // Legacy fallback — a hidden, off-screen textarea + document.execCommand.
  try {
    const ta = document.createElement('textarea');
    ta.value = str;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, str.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    if (ok) {
      if (!silent) toast.success(label);
      return true;
    }
  } catch (_) { /* ignore, fall through */ }

  // Last resort — show the value so the user can copy manually.
  if (!silent) {
    toast.error("Couldn't copy automatically", {
      description: str.length > 200 ? str.slice(0, 200) + '…' : str,
      duration: 8000,
    });
  }
  return false;
}
